import React, { useState } from "react";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { Row, Col, Card } from "react-bootstrap";
import Availability from "components/orchid-pro/availability-tag/AvailabilityTag";
import OrchidProAvatar from "components/orchid-pro-avatar";
import ShowMore from "components/show-more";
import { ReactComponent as Quotation } from "assets/svg/quotation.svg";
import {
  getOrchidProNameAndAllGraduateDegreesString,
  getOrchidProTypeStringFromNewArr,
} from "utils/formatter/orchidPro";
import getOrchidProDisplayName from "utils/getOrchidProDisplayName";
import ToggleVisibility from "./toggle-visibility";
import OrchidVerifiedTag from "components/orchid-pro/orchid-verified-tag";
import capitalize from "utils/capitalize";
import getClasses from "utils/getClasses";
import styles from "./OrchidProFullProfile.module.scss";
import { AmplitudePageName, track } from "utils/amplitude";

const ToggleDataArr = ({
  arr,
  showAll,
  setShowAll,
  showAllText,
  showLessText,
  onToggle,
}) => {
  return arr.length <= 6 ? (
    <Row>
      <Col md={6} className={"pl-0"}>
        <ul className={styles["full-profile__ul"]}>
          {arr.map((el, index) => index <= 2 && <li key={index}>{el}</li>)}
        </ul>
      </Col>
      <Col md={6} className={"pl-0"}>
        <ul className={styles["full-profile__ul"]}>
          {arr.map((el, index) => index >= 3 && <li key={index}>{el}</li>)}
        </ul>
      </Col>
    </Row>
  ) : (
    <ToggleVisibility
      showAll={showAll}
      setShowAll={setShowAll}
      showAllText={showAllText}
      showLessText={showLessText}
      onToggle={onToggle}
    >
      <Row>
        <Col md={6} className={"pl-0"}>
          <ul className={styles["full-profile__ul"]}>
            {arr.map((el, index) => index <= 2 && <li key={index}>{el}</li>)}
          </ul>
        </Col>
        <Col md={6} className={"pl-0"}>
          <ul className={styles["full-profile__ul"]}>
            {arr.map(
              (el, index) =>
                index >= 3 && index <= 5 && <li key={index}>{el}</li>,
            )}
          </ul>
        </Col>
      </Row>
      {arr.length > 6 && showAll && (
        <Row>
          <Col md={6} className={"pl-0"}>
            <ul className={styles["full-profile__ul"]}>
              {arr.map(
                (el, index) =>
                  index >= 6 &&
                  index < arr.length / 2 + 3 && <li key={index}>{el}</li>,
              )}
            </ul>
          </Col>
          <Col md={6} className={"pl-0"}>
            <ul className={styles["full-profile__ul"]}>
              {arr.map(
                (el, index) =>
                  index >= arr.length / 2 + 3 && <li key={index}>{el}</li>,
              )}
            </ul>
          </Col>
        </Row>
      )}
    </ToggleVisibility>
  );
};

interface Props {
  orchidPro?: OrchidPro;
  amplitudePageName: AmplitudePageName;
}

const OrchidProFullProfile: React.FC<Props> = ({
  orchidPro,
  amplitudePageName,
}) => {
  const { isPhonePortrait } = usePhonePortraitContext();
  const [showAllIssues, setShowAllIssues] = useState(false);
  const [showAllTreatment, setShowAllTreatment] = useState(false);
  const [showAllInsurance, setShowAllInsurance] = useState(false);

  return !orchidPro ? null : (
    <>
      {!isPhonePortrait && (
        <Row className="mb-4">
          <div className={styles["full-profile__brief-info"]}>
            <OrchidProAvatar orchidPro={orchidPro} />
            <span className="ml-4">
              <h1>
                <div className="deep-blue rg-24 mt-0 mb-2">
                  {getOrchidProNameAndAllGraduateDegreesString(orchidPro)}
                </div>
                {!orchidPro.orchid_pro_types
                  ?.length ? null : orchidPro.cognito_sub ===
                  "9929e6ac-8d68-4fba-8208-9b14ec05f132" ? (
                  <p className="rg-14 gray01 mb-0">
                    Licensed Occupational Therapist
                  </p>
                ) : (
                  <p className="rg-14 gray01 mb-0">
                    {getOrchidProTypeStringFromNewArr(orchidPro)}
                  </p>
                )}
                {!orchidPro.licenses?.length ? (
                  <p />
                ) : (
                  <p className="bd-14">
                    {orchidPro.licenses
                      .filter((license) => license.type)
                      .map((license, index) => {
                        return (
                          <span key={index}>
                            {index > 0 && ", "}
                            {license.type}
                          </span>
                        );
                      })}
                  </p>
                )}
              </h1>

              <div className={"d-flex"}>
                {orchidPro.can_accept_payment && (
                  <Availability
                    className={"mr-2"}
                    orchid_pro_cognito_sub={orchidPro.cognito_sub}
                  />
                )}
                <OrchidVerifiedTag />
              </div>
            </span>
          </div>
        </Row>
      )}
      {orchidPro.introduction_video && (
        <div
          className={getClasses(
            !isPhonePortrait && "rounded-lg",
            `embed-responsive embed-responsive-16by9`,
          )}
          style={{
            backgroundColor: "var(--dark)",
          }}
        >
          <iframe
            title="placeholder"
            src={orchidPro.introduction_video}
            width="640"
            height="360"
            frameBorder="0"
            allow="autoplay; fullscreen"
            allowFullScreen
          ></iframe>
        </div>
      )}
      {isPhonePortrait && (
        <>
          <Row className="pt-4">
            <Col xs={7}>
              <p className="bd-17 mb-2">
                About {getOrchidProDisplayName(orchidPro)}
              </p>
              {!orchidPro.orchid_pro_types?.length ? null : (
                <p className="rg-13-footnote gray01 mb-1">
                  {getOrchidProTypeStringFromNewArr(orchidPro)}
                </p>
              )}
              {!orchidPro.licenses?.length ? (
                <p />
              ) : (
                <p className="bd-14">
                  {orchidPro.licenses
                    .filter((license) => license.type)
                    .map((license, index) => {
                      return (
                        <span key={index}>
                          {index > 0 && ", "}
                          {license.type}
                        </span>
                      );
                    })}
                </p>
              )}
            </Col>
            <Col xs={5} className="d-flex justify-content-end">
              <OrchidProAvatar
                orchidPro={orchidPro}
                style={{
                  width: "2.25rem",
                  height: "2.25rem",
                }}
              />
            </Col>
          </Row>
          <Row className="pb-4">
            <Col>
              <ShowMore content={orchidPro.biography} lines={5} />
            </Col>
          </Row>
        </>
      )}

      {!isPhonePortrait && (
        <>
          <div className={styles["full-profile__left-content"]}>
            <h4 className="rg-24 mt-4 questrial deep-blue">
              About {getOrchidProDisplayName(orchidPro)}
            </h4>
            <ShowMore content={orchidPro.biography} lines={5} />
          </div>
          <Row>
            {orchidPro.years_of_practice && (
              <Col md={6} xs={12} className={"pl-0"}>
                <p
                  className={
                    !isPhonePortrait
                      ? styles["full-profile__sub-title"]
                      : "mb-0 rg-13-footnote gray01"
                  }
                >
                  Year of Experience
                </p>
                <p className={!isPhonePortrait ? "rg-16" : "rg-17"}>
                  {orchidPro.years_of_practice}
                </p>
              </Col>
            )}

            {orchidPro.languages?.length > 0 && (
              <Col md={6} xs={12} className={"pl-0"}>
                <p
                  className={
                    !isPhonePortrait
                      ? styles["full-profile__sub-title"]
                      : "mb-0 rg-13-footnote gray01"
                  }
                >
                  Languages
                </p>
                <p className={!isPhonePortrait ? "rg-16" : "rg-17"}>
                  {orchidPro.languages.join(", ")}
                </p>
              </Col>
            )}
          </Row>

          <hr className={styles["full-profile__hr"]} />
        </>
      )}

      <div
        className={
          !isPhonePortrait
            ? styles["full-profile__left-content"]
            : styles["full-profile__left-content--phone"]
        }
      >
        {(orchidPro.cognito_sub === "6e197c58-9c0a-4990-be07-ff5e8332190a" ||
          orchidPro.cognito_sub === "bb79a762-144a-4085-8475-22d4b0ba947a") && (
          <>
            <div className={styles["full-profile__title"]}>Testimonials</div>
            <p
              className={
                !isPhonePortrait
                  ? styles["full-profile__sub-title"]
                  : "mb-0 rg-13-footnote gray01"
              }
            >
              1 Testimonial
            </p>
            <Card className={styles["full-profile__card"]}>
              <Card.Body className={styles["full-profile__card-body"]}>
                <div className={styles["full-profile__quotation"]}>
                  <Quotation />
                </div>
                <ShowMore
                  content={
                    "I wanted to share my experience with Karen. I was so nervous starting therapy but working with Karen was a blessing. She made me feel heard, cared for, and she was empathetic towards my struggles. I felt at ease and always left each session with a plan as to what I'd like to work on. I can't thank her enough for helping me through this difficult season in my life."
                  }
                  lines={3}
                />
                <p className={"rg-12 gray01 smoothed mt-3 mb-0"}>
                  March 16, 2022 • Verified Orchid Client
                </p>
              </Card.Body>
            </Card>
            <hr className={styles["full-profile__hr"]} />
          </>
        )}

        {orchidPro.services_offered && (
          <>
            <div className={styles["full-profile__title"]}>
              Appointment Type
            </div>
            <div>{capitalize(orchidPro.services_offered)}</div>
            <hr className={styles["full-profile__hr"]} />
          </>
        )}

        {orchidPro.focus_issues?.length > 0 && (
          <div>
            <div className={styles["full-profile__title"]}>Focus Issues</div>
            <ToggleDataArr
              arr={orchidPro.focus_issues}
              showAll={showAllIssues}
              setShowAll={setShowAllIssues}
              showAllText={"View all focus issues"}
              showLessText={"View fewer focus issues"}
              onToggle={() =>
                track(
                  amplitudePageName,
                  "view-all-focus-issues-button",
                  "click",
                )
              }
            />
            <hr className={styles["full-profile__hr"]} />
          </div>
        )}
        {orchidPro.treatment_approaches?.length > 0 && (
          <div>
            <div className={styles["full-profile__title"]}>
              Treatment Approach
            </div>
            <ToggleDataArr
              arr={orchidPro.treatment_approaches}
              showAll={showAllTreatment}
              setShowAll={setShowAllTreatment}
              showAllText={"View all treatment approaches"}
              showLessText={"View fewer treatment approaches"}
              onToggle={() =>
                track(
                  amplitudePageName,
                  "view-all-treatment-approaches-button",
                  "click",
                )
              }
            />
            <hr className={styles["full-profile__hr"]} />
          </div>
        )}

        {(orchidPro.clientele_age?.length > 0 ||
          orchidPro.clientele_community?.length > 0 ||
          orchidPro.modality?.length > 0) && (
          <>
            <div className={styles["full-profile__title"]}>Client Focus</div>
            <Row className={"mb-3"}>
              <Col md={6} className={"pl-0"}>
                {orchidPro.clientele_age?.length > 0 && (
                  <>
                    <p
                      className={
                        !isPhonePortrait
                          ? styles["full-profile__sub-title"]
                          : "mb-0 rg-13-footnote gray01"
                      }
                    >
                      Age Groups
                    </p>
                    <ul className={styles["full-profile__ul"]}>
                      {orchidPro.clientele_age.map((ageGroup, index) => (
                        <li key={index}>{ageGroup}</li>
                      ))}
                    </ul>
                  </>
                )}
              </Col>
              <Col md={6} className={"pl-0"}>
                {orchidPro.modality?.length > 0 && (
                  <>
                    <p
                      className={
                        !isPhonePortrait
                          ? styles["full-profile__sub-title"]
                          : "mb-0 rg-13-footnote gray01"
                      }
                    >
                      Modality
                    </p>
                    <ul className={styles["full-profile__ul"]}>
                      {orchidPro.modality.map((modality, index) => (
                        <li key={index}>{capitalize(modality)}</li>
                      ))}
                    </ul>
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <Col md={6} className={"pl-0"}>
                {orchidPro.clientele_community?.length > 0 && (
                  <>
                    <p
                      className={
                        !isPhonePortrait
                          ? styles["full-profile__sub-title"]
                          : "mb-0 rg-13-footnote gray01"
                      }
                    >
                      Communities
                    </p>
                    <ul className={styles["full-profile__ul"]}>
                      {orchidPro.clientele_community.map((community, index) => (
                        <li key={index}>{community}</li>
                      ))}
                    </ul>
                  </>
                )}
              </Col>
            </Row>
            <hr className={styles["full-profile__hr"]} />
          </>
        )}

        <div className={styles["full-profile__title"]}>
          Professional Credentials
        </div>
        <Row>
          <Col md={6} className={"pl-0"}>
            {orchidPro.national_provider_identifier && (
              <>
                <p
                  className={
                    !isPhonePortrait
                      ? styles["full-profile__sub-title"]
                      : "mb-0 rg-13-footnote gray01"
                  }
                >
                  Individual NPI Number
                </p>
                <p className={!isPhonePortrait ? "rg-16" : "rg-17"}>
                  {orchidPro.national_provider_identifier}
                </p>
              </>
            )}
            <p
              className={
                !isPhonePortrait
                  ? styles["full-profile__sub-title"]
                  : "mb-0 rg-13-footnote gray01"
              }
            >
              License Details
            </p>

            <div
              className={getClasses(
                "mb-3",
                !isPhonePortrait ? "rg-16" : "rg-17",
              )}
            >
              {orchidPro.licenses?.length > 0
                ? [
                    ...new Set(
                      (orchidPro.licenses || []).map(
                        ({ conferring_entity }) => conferring_entity,
                      ),
                    ),
                  ].join(", ")
                : orchidPro.state_licenses?.length > 0
                ? orchidPro.state_licenses.map((license, index) => {
                    return (
                      <p key={index} className="d-inline mb-0">
                        {index > 0 && ", "}
                        {license.state}
                      </p>
                    );
                  })
                : "NA"}
            </div>
            {orchidPro.organization && (
              <>
                <p
                  className={
                    !isPhonePortrait
                      ? styles["full-profile__sub-title"]
                      : "mb-0 rg-13-footnote gray01"
                  }
                >
                  Organization / Affiliation
                </p>
                <p className={!isPhonePortrait ? "rg-16" : "rg-17"}>
                  {orchidPro.organization}
                </p>
              </>
            )}
          </Col>
          <Col className={"pl-0"}>
            {orchidPro.licenses.some(
              (license) => license.supervision?.has_supervision,
            )
              ? orchidPro.licenses
                  .filter((license) => license.supervision?.has_supervision)
                  .map((license, index) => (
                    <div key={index}>
                      <p
                        className={
                          !isPhonePortrait
                            ? styles["full-profile__sub-title"]
                            : "mb-0 rg-13-footnote gray01"
                        }
                      >
                        Supervisor Information
                      </p>
                      <div className={"gray01 rg-14"}>Supervisor Name:</div>
                      <p>{license.supervision.name}</p>
                      {license.supervision.npi && (
                        <>
                          <div className={"gray01 rg-14"}>
                            Supervisor NPI Number:
                          </div>
                          <p>{license.supervision.npi}</p>
                        </>
                      )}
                      {license.supervision.phone && (
                        <>
                          <div className={"gray01 rg-14"}>
                            Supervisor Contact Number:
                          </div>
                          <p>{license.supervision.phone}</p>
                        </>
                      )}
                    </div>
                  ))
              : null}
          </Col>
        </Row>
        <hr className={styles["full-profile__hr"]} />
        <div className={styles["full-profile__title"]}>
          Education & Training
        </div>

        {(orchidPro.graduate && orchidPro.graduate?.length > 0) ||
        (orchidPro.undergraduate && orchidPro.undergraduate?.length > 0) ? (
          <>
            <p
              className={
                !isPhonePortrait
                  ? styles["full-profile__sub-title"]
                  : "mb-0 rg-13-footnote gray01"
              }
            >
              Education
            </p>
            <ul className={styles["full-profile__ul"]}>
              {orchidPro.graduate?.map((grad, index) => (
                <li key={index}>
                  {grad.conferring_entity}, {grad.degree}
                  {grad.year && `, ${grad.year}`}
                </li>
              ))}
            </ul>
            <ul className={styles["full-profile__ul"]}>
              {orchidPro.undergraduate?.map((undergrad, index) => (
                <li key={index}>
                  {undergrad.conferring_entity}, {undergrad.degree}
                  {undergrad.year && `, ${undergrad.year}`}
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="mb-3">
            {orchidPro.medical_degree ? (
              <>
                <p
                  className={
                    !isPhonePortrait
                      ? styles["full-profile__sub-title"]
                      : "mb-0 rg-13-footnote gray01"
                  }
                >
                  Education
                </p>
                <p
                  className={getClasses(
                    "mb-0",
                    !isPhonePortrait ? "rg-16" : "rg-17",
                  )}
                >
                  {orchidPro.gradudate_institution
                    ? orchidPro.gradudate_institution +
                      ", " +
                      orchidPro.medical_degree
                    : orchidPro.medical_degree}
                </p>
              </>
            ) : null}
            {orchidPro.degrees && orchidPro.degrees?.length > 0
              ? orchidPro.degrees.map((degree, index) => {
                  return (
                    <div
                      className={getClasses(
                        "m-0",
                        !isPhonePortrait ? "rg-16" : "rg-17",
                      )}
                      key={index}
                    >
                      {/* {index > 0 && ", "} */}
                      {degree.institution ? degree.institution + ", " : null}
                      {degree.degree}
                    </div>
                  );
                })
              : null}
          </div>
        )}

        {((orchidPro.residency_json && orchidPro.residency_json.length > 0) ||
          (orchidPro.fellowships_json &&
            orchidPro.fellowships_json.length > 0) ||
          orchidPro.residency ||
          orchidPro.fellowships) && (
          <p
            className={
              !isPhonePortrait
                ? styles["full-profile__sub-title"]
                : "mb-0 rg-13-footnote gray01"
            }
          >
            Training
          </p>
        )}

        {orchidPro.residency_json && orchidPro.residency_json?.length > 0 ? (
          <ul className={styles["full-profile__ul"]}>
            {orchidPro.residency_json.map((residency, index) => (
              <li key={index}>
                {residency.conferring_entity}
                {residency.program && `/ ${residency.program}`}, Residency
                {residency.year && `, ${residency.year}`}
              </li>
            ))}
          </ul>
        ) : (
          orchidPro.residency && (
            <ul className={styles["full-profile__ul"]}>
              <li>{orchidPro.residency}</li>
            </ul>
          )
        )}

        {orchidPro.fellowships_json &&
        orchidPro.fellowships_json?.length > 0 ? (
          <ul className={styles["full-profile__ul"]}>
            {orchidPro.fellowships_json.map((fellowship, index) => (
              <li key={index}>
                {fellowship.conferring_entity}
                {fellowship.program && `/ ${fellowship.program}`}, Fellowship
                {fellowship.year && `, ${fellowship.year}`}
              </li>
            ))}
          </ul>
        ) : (
          orchidPro.fellowships && (
            <ul className={styles["full-profile__ul"]}>
              <li>{orchidPro.fellowships}</li>
            </ul>
          )
        )}
        {orchidPro.board_certifications?.length > 0 && (
          <>
            <p
              className={
                !isPhonePortrait
                  ? styles["full-profile__sub-title"]
                  : "mb-0 rg-13-footnote gray01"
              }
            >
              Board Certifications
            </p>
            <ul className={styles["full-profile__ul"]}>
              {orchidPro.board_certifications.map(
                (boardCertification, index) => (
                  <li key={index}>{boardCertification.board || "NA"}</li>
                ),
              )}
            </ul>
          </>
        )}
        <hr className={styles["full-profile__hr"]} />
        {orchidPro.insurance_accepted?.length > 0 && (
          <>
            <div className={styles["full-profile__title"]}>Insurance</div>
            <div className={styles["full-profile__sub-title"]}>
              Accepted Insurance Plans
            </div>
            <ToggleDataArr
              arr={orchidPro.insurance_accepted}
              showAll={showAllInsurance}
              setShowAll={setShowAllInsurance}
              showAllText={"View all insurance plans"}
              showLessText={"View fewer insurance plans"}
              onToggle={() =>
                track(
                  amplitudePageName,
                  "view-all-insurance-plans-button",
                  "click",
                )
              }
            />
            <hr className={styles["full-profile__hr"]} />
          </>
        )}

        <div className={styles["full-profile__title"]}>Practice Settings</div>
        <Row>
          {orchidPro.business_entity_name && (
            <Col md={6} className={"pl-0"}>
              <p
                className={
                  !isPhonePortrait
                    ? styles["full-profile__sub-title"]
                    : "mb-0 rg-13-footnote gray01"
                }
              >
                Business name
              </p>
              <p className={!isPhonePortrait ? "rg-16" : "rg-17"}>
                {orchidPro.business_entity_name}
              </p>
            </Col>
          )}
          {!orchidPro.show_address_to_public
            ? null
            : orchidPro.cognito_sub !==
                "6c498d63-b8b9-4e34-9e34-acc5bbbe8928" && (
                <Col md={6} className={"pl-0"}>
                  <p
                    className={
                      !isPhonePortrait
                        ? styles["full-profile__sub-title"]
                        : "mb-0 rg-13-footnote gray01"
                    }
                  >
                    Business Address
                  </p>
                  <p className={!isPhonePortrait ? "rg-16" : "rg-17"}>
                    {orchidPro.office_address_line1 ? (
                      <>
                        {orchidPro.cognito_sub ===
                        "ad4a206e-a435-415a-829d-8a9963fa8819" // Kate	Merrill Evans does not want to show this line
                          ? null
                          : orchidPro.office_address_line1}
                        {orchidPro.office_address_line2
                          ? ", " + orchidPro.office_address_line2
                          : null}
                        {orchidPro.cognito_sub ===
                        "ad4a206e-a435-415a-829d-8a9963fa8819" ? null : (
                          <br />
                        )}
                        {orchidPro.office_city},&nbsp;
                        {orchidPro.office_state}&nbsp;
                        {orchidPro.office_zipcode}
                      </>
                    ) : (
                      "NA"
                    )}
                  </p>
                </Col>
              )}
        </Row>
      </div>
    </>
  );
};

export default OrchidProFullProfile;
